import { DestinationItem } from '@tableau/nested-project-explorer';
import { ActivitySpinnerWidget, ButtonType, ButtonWidget, DropdownButton, DropdownButtonType, TabStyles, TreeItem, TreeWidget } from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { projectPickerFieldStyles, projectPickerStyles } from '~src/projects/components/styles/ProjectPickerStyles';

import { ProjectTreeNodeContent } from './ProjectTreeNodeContent';
import {
  projectDropdownButtonClass,
  projectDropdownContainerClass,
  projectDropdownLabelClass,
  projectPickerHeaderClass,
  projectPickerSelectAllRowClass,
  projectPickerTabsClass,
  projectTreeClass,
} from './RoutingFilterProjectSelectorStyles';

type TreeItemWithFilterValue = TreeItem & { filterValue?: string };

const projectDropdownLabelId = 'routing-filter-project-dropdown-label';
const projectDropdownPopupId = 'routing-filter-project-dropdown-popup';
const treeId = 'routing-filter-project-tree';

export type RoutingFilterProjectSelectorProps = Readonly<{
  projects: ReadonlyArray<DestinationItem> | null;
  projectIds: Set<string>;
  onProjectIdsChange: (projectIds: Set<string>) => void;
  readOnly?: boolean;
  // Tree ids of projects the server flagged as conflicting. A row is annotated
  // only while it is still selected — deselecting it removes the annotation.
  conflictingProjectIds?: ReadonlySet<string>;
}>;

export function RoutingFilterProjectSelector(props: RoutingFilterProjectSelectorProps) {
  const readOnly = props.readOnly === true;
  const selectedCount = props.projectIds.size;
  const buttonText = selectedCount === 0
    ? Messages.bridgeSiteSettingsRoutingFilterNoProjectSelected()
    : Messages.bridgeSiteSettingsRoutingFilterSelectedProjects({ COUNT: String(selectedCount) });

  const items = React.useMemo(
    () => props.projects ? convertProjectsToTreeItems(props.projects) : null,
    [props.projects]
  );
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const { onProjectIdsChange, projectIds, conflictingProjectIds } = props;

  const selectableValues = React.useMemo(() => {
    const values: string[] = [];
    for (const item of items ?? []) {
      const value = String(item.value);
      if (!item.disabled) {
        values.push(value);
      }
    }
    return values;
  }, [items]);

  const childrenByParent = React.useMemo(() => {
    const map: Record<string, string[]> = Object.create(null);
    for (const item of items ?? []) {
      const value = String(item.value);
      const parent = item.parentValue ? String(item.parentValue) : '';
      if (!map[parent]) {
        map[parent] = [];
      }
      map[parent].push(value);
    }
    return map;
  }, [items]);

  const isParentNode = React.useCallback((value: string) => {
    return (childrenByParent[value]?.length ?? 0) > 0;
  }, [childrenByParent]);

  const getDescendants = React.useCallback((root: string) => {
    const out: string[] = [];
    const stack: string[] = (childrenByParent[root] ?? []).slice();
    while (stack.length) {
      const cur = stack.pop() as string;
      out.push(cur);
      const kids = childrenByParent[cur];
      if (kids && kids.length) {
        for (let i = 0; i < kids.length; i++) {
          stack.push(kids[i]);
        }
      }
    }
    return out;
  }, [childrenByParent]);

  const toggleNestedSelection = React.useCallback((root: string) => {
    const descendants = getDescendants(root);
    const nodeAndDescendants = [root, ...descendants];
    const allSelected = nodeAndDescendants.every(id => projectIds.has(id));
    const next = new Set(projectIds);
    for (const id of nodeAndDescendants) {
      if (allSelected) {
        next.delete(id);
      } else {
        next.add(id);
      }
    }
    onProjectIdsChange(next);
  }, [getDescendants, onProjectIdsChange, projectIds]);

  const toggleProjectId = React.useCallback((value: string) => {
    const next = new Set(projectIds);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onProjectIdsChange(next);
  }, [onProjectIdsChange, projectIds]);

  const selectAll = React.useCallback(() => {
    onProjectIdsChange(new Set(selectableValues));
  }, [onProjectIdsChange, selectableValues]);

  const deselectAll = React.useCallback(() => {
    onProjectIdsChange(new Set());
  }, [onProjectIdsChange]);

  const renderedItemsToShow = React.useMemo(() => {
    if (!items) {
      return null;
    }
    const filtered = activeTabIndex === 0 ? items : buildFlatSelectedItems(items, projectIds);
    return filtered.map((item: TreeItemWithFilterValue) => {
      const value = String(item.value);
      const name = item.filterValue ?? value;
      const checked = projectIds.has(value);
      const disabled = !!item.disabled;
      const showNestedActions = !readOnly && activeTabIndex === 0 && isParentNode(value);
      // Highlight a conflicting project on both the All Projects and Selection
      // tabs, but only while it remains selected; deselecting it drops the
      // highlight (an unselected project can't contribute to a conflict).
      const isConflict = checked && (conflictingProjectIds?.has(value) ?? false);
      return {
        ...item,
        content: (
          <ProjectTreeNodeContent
            value={value}
            name={name}
            checked={checked}
            disabled={disabled}
            readOnly={readOnly}
            showNestedActions={showNestedActions}
            isConflict={isConflict}
            onToggle={toggleProjectId}
            onToggleNested={toggleNestedSelection}
          />
        ),
      } as TreeItem;
    });
  }, [activeTabIndex, items, isParentNode, projectIds, readOnly, conflictingProjectIds, toggleNestedSelection, toggleProjectId]);

  return (
    <div className={projectDropdownContainerClass}>
      <div id={projectDropdownLabelId} className={projectDropdownLabelClass}>{Messages.bridgeSiteSettingsRoutingFilterFromProject()}</div>
      <DropdownButton
        label={''}
        testId={'routing-filter-project-multiselect-dropdown'}
        type={DropdownButtonType.Outline}
        aria-haspopup={'dialog'}
        aria-controls={projectDropdownPopupId}
        aria-labelledby={projectDropdownLabelId}
        className={projectDropdownButtonClass}
        popupContent={
          <div
            id={projectDropdownPopupId}
            role={'dialog'}
            aria-label={Messages.bridgeSiteSettingsRoutingFilterSelectProjects()}
            className={classes(projectPickerStyles, projectPickerFieldStyles)}
            style={{ height: 320, width: 330, position: 'relative' }}
          >
            {renderedItemsToShow ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className={projectPickerHeaderClass}>
                  <div className={projectPickerTabsClass} role={'tablist'}>
                    <ButtonWidget
                      buttonType={activeTabIndex === 0 ? ButtonType.Primary : ButtonType.Outline}
                      onClick={() => setActiveTabIndex(0)}
                      style={{ width: '50%' }}
                    >
                      {Messages.bridgeSiteSettingsRoutingFilterAllProjects()}
                    </ButtonWidget>
                    <ButtonWidget
                      buttonType={activeTabIndex === 1 ? ButtonType.Primary : ButtonType.Outline}
                      onClick={() => setActiveTabIndex(1)}
                      style={{ width: '50%' }}
                    >
                      {Messages.bridgeSiteSettingsRoutingFilterSelection({ COUNT: String(selectedCount) })}
                    </ButtonWidget>
                  </div>
                  {!readOnly && (
                    <div className={projectPickerSelectAllRowClass}>
                      <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={selectAll}>
                        {Messages.bridgeSiteSettingsRoutingFilterSelectAll()}
                      </ButtonWidget>
                      <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={deselectAll}>
                        {Messages.bridgeSiteSettingsRoutingFilterUnselectAll()}
                      </ButtonWidget>
                    </div>
                  )}
                </div>
                <TreeWidget
                  id={treeId}
                  label={Messages.bridgeSiteSettingsRoutingFilterAllProjects()}
                  items={renderedItemsToShow}
                  selectedValue={null}
                  itemFilterFn={projectTreeItemFilterFn}
                  className={projectTreeClass}
                  style={{ flex: 1, minHeight: 0 }}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', marginTop: TabStyles.Spacing.ExtraLargeSpace }}>
                <ActivitySpinnerWidget testId={'routing-filter-projects-loading'} />
              </div>
            )}
          </div>
        }
      >
        {buttonText}
      </DropdownButton>
    </div>
  );
}

function buildFlatSelectedItems(items: ReadonlyArray<TreeItem>, selectedLeafValues: Set<string>): Array<TreeItem> {
  if (selectedLeafValues.size === 0) {
    return [];
  }

  const byId: Record<string, TreeItem> = Object.create(null);
  for (const item of items) {
    byId[String(item.value)] = item;
  }

  const selected: TreeItem[] = [];
  selectedLeafValues.forEach((id) => {
    const key = String(id);
    const original = byId[key];
    if (original) {
      selected.push({ ...original, parentValue: undefined });
    } else {
      selected.push({ value: key, parentValue: undefined, content: key, filterValue: key } as TreeItemWithFilterValue);
    }
  });

  selected.sort((a: TreeItemWithFilterValue, b: TreeItemWithFilterValue) => {
    const al = String(a.filterValue ?? a.value ?? '').toLowerCase();
    const bl = String(b.filterValue ?? b.value ?? '').toLowerCase();
    return al.localeCompare(bl);
  });

  return selected;
}

function convertProjectsToTreeItems(projects: ReadonlyArray<DestinationItem>): Array<TreeItemWithFilterValue> {
  return projects.map((p: DestinationItem) => ({
    value: p.id,
    parentValue: p.parentId ? String(p.parentId) : undefined,
    disabled: p.status !== 'valid',
    content: p.name,
    filterValue: p.name,
  }));
}

function projectTreeItemFilterFn(item: TreeItemWithFilterValue, searchText: string) {
  const s = searchText.toLowerCase();
  const filterValue = item.filterValue ?? '';
  return filterValue.toLowerCase().includes(s) || String(item.value).toLowerCase().includes(s);
}
